import React, { useState } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import ChartHeader from "./ChartHeader";

function ChartOKNOKRework({ title, subtitle, data, detailsDesc, orderNumber = "", dateRange = ""  }) {
    const [hoveredSegment, setHoveredSegment] = useState(null);
    const [showDetailChart, setShowDetailChart] = useState(false);
    const [detailHoveredSegment, setDetailHoveredSegment] = useState(null);

    const defaultData = {
        totalOK: "N/A",
        reworkDetails: {
            A: "N/A",
            mainField: 0
        },
        nokDetails: {
            A: "N/A",
            mainField: 0
        },
        totalChecked: "N/A"
    };

    const inputData = data || defaultData;

    // ===== WARTOŚCI Z PANELU NAD WYKRESEM =====
    const totalOK = inputData.totalOK || 0;                           // OK bef. rework
    const Rework = inputData.reworkDetails?.mainField || 0;           // Rework
    const NOK = inputData.nokDetails?.mainField || 0;                 // NOK
    const grandTotal = inputData.totalChecked || 0;                   // Total Checked

    // Total dla wykresu (sum wartości dla obliczeń wewnętrznych)
    const total = totalOK + Rework + NOK || 1;

    // ===== PROCENTY DLA LEGENDY =====
    // Procenty na podstawie wartości z panelu / Total Checked
    const okPercentage = grandTotal > 0 ? Math.round((totalOK / grandTotal) * 100 * 10) / 10 : 0;
    const reworkPercentage = grandTotal > 0 ? Math.round((Rework / grandTotal) * 100 * 10) / 10 : 0;
    const nokPercentage = grandTotal > 0 ? Math.round((NOK / grandTotal) * 100 * 10) / 10 : 0;

    // ===== SZCZEGÓŁY (do tooltipów) =====
    const totalReworkDetails = Object.entries(inputData.reworkDetails || {})
        .filter(([key]) => key !== 'mainField')
        .reduce((sum, [, value]) => sum + value, 0);

    const totalNOKDetails = Object.entries(inputData.nokDetails || {})
        .filter(([key]) => key !== 'mainField')
        .reduce((sum, [, value]) => sum + value, 0);

    // Przygotuj dane dla głównego wykresu
    const mainChartData = [
        {
            name: 'OK bef. rework',
            value: totalOK,
            percentage: okPercentage,
            displayPercentage: okPercentage
        },
        {
            name: 'Rework',
            value: Rework,
            percentage: reworkPercentage,
            displayPercentage: reworkPercentage,
            details: inputData.reworkDetails || {}
        },
        {
            name: 'NOK',
            value: NOK,
            percentage: nokPercentage,
            displayPercentage: nokPercentage,
            details: inputData.nokDetails || {}
        }
    ].filter(item => item.value > 0); // Filtruj puste kategorie

    // Przygotuj dane dla szczegółowego wykresu Rework/NOK
    const detailChartData = hoveredSegment?.name === 'Rework'
        ? Object.entries(inputData.reworkDetails || {})
            .filter(([key, value]) => key !== 'mainField' && value > 0)
            .map(([name, count]) => ({
                name,
                value: count,
                percentage: totalReworkDetails > 0 ? Math.round((count / totalReworkDetails) * 100 * 10) / 10 : 0
            }))
        : Object.entries(inputData.nokDetails || {})
            .filter(([key, value]) => key !== 'mainField' && value > 0)
            .map(([name, count]) => ({
                name,
                value: count,
                percentage: totalNOKDetails > 0 ? Math.round((count / totalNOKDetails) * 100 * 10) / 10 : 0
            }));

    // Kolory
    const colors = {
        'OK bef. rework': '#4CAF50',      // Zielony
        'Rework': '#E07A00',  // Pomarańczowy
        'NOK': '#DC143C'      // Czerwony
    };

    // Kolory dla szczegółowych Rework/NOK
    const detailColors = {
        'a': '#FFD700', 'b': '#C71585', 'c': '#000080', 'd': '#008B8B',
        'e': '#20B2AA', 'f': '#32CD32', 'g': '#FF69B4', 'h': '#4B0082',
        'i': '#FBCEB1', 'j': '#FA8072', 'k': '#00CED1', 'l': '#87CEEB',
        'm': '#008000', 'n': '#800080', 'o': '#DC143C', 'p': '#00FFFF',
        'A': '#008B8B', 'B': '#FF7F00', 'C': '#800080', 'D': '#000080',
        'E': '#FBCEB1', 'F': '#FFD700', 'G': '#20B2AA', 'H': '#4B0082',
        'I': '#32CD32', 'J': '#C71585', 'K': '#FA8072', 'L': '#008000',
        'M': '#87CEEB', 'N': '#FF69B4', 'O': '#00CED1', 'P': '#DC143C'
    };

    // Sprawdź czy segment jest bardzo mały (≤ 3%)
    const isVerySmallSegment = (percentage) => percentage <= 3;

    // Sprawdź czy szczegółowy wykres powinien być aktywny
    const shouldShowDetailChart = hoveredSegment &&
        (hoveredSegment.name === 'Rework' || hoveredSegment.name === 'NOK') &&
        isVerySmallSegment(hoveredSegment.percentage) &&
        showDetailChart;

    // Custom tooltip dla głównego wykresu
    const MainCustomTooltip = ({ active, payload }) => {
        if (shouldShowDetailChart) {
            return null;
        }

        if (active && payload && payload.length) {
            const data = payload[0].payload;

            return (
                <div style={{
                    backgroundColor: 'white',
                    padding: '12px',
                    border: '1px solid #ccc',
                    borderRadius: '6px',
                    fontSize: '12px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    minWidth: '240px',
                    zIndex: 1000,
                    fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                }}>
                    {/* GÓRNY WIERSZ: Główne info */}
                    <div style={{
                        fontSize: '12px',
                        fontWeight: '600',
                        color: 'rgb(52, 71, 103)',
                        marginBottom: '8px',
                        lineHeight: '1.4'
                    }}>
                        Total {data.name}: {data.value} parts
                    </div>
                    <div style={{
                        fontSize: '12px',
                        fontWeight: '600',
                        color: 'rgb(52, 71, 103)',
                        marginBottom: '8px',
                        lineHeight: '1.4'
                    }}>
                        Percentage: {data.percentage}%
                    </div>
                    <div style={{
                        fontSize: '12px',
                        fontWeight: '600',
                        color: 'rgb(52, 71, 103)',
                        marginBottom: '8px',
                        lineHeight: '1.4'
                    }}>
                        Total defects: {data.name === 'NOK' ? totalNOKDetails : totalReworkDetails}
                    </div>
                    {/* Szczegóły Rework/NOK gdy jest więcej niż 3% */}
                    {(data.name === 'Rework' || data.name === 'NOK') && data.details && !isVerySmallSegment(data.percentage) && (
                        <div>
                            <hr style={{ margin: '8px 0', border: 'none', borderTop: '1px solid #eee' }} />
                            <p style={{ margin: '0 0 4px 0', fontSize: '12px', fontWeight: '600', color: '#666' }}>
                                {data.name} Defect details:
                            </p>
                            {Object.entries(data.details)
                                .filter(([key, value]) => key !== 'mainField' && value > 0)
                                .map(([type, count]) => {
                                    const description = detailsDesc?.find(el => el.label === (data.name === "NOK" ? type.toUpperCase() : type))?.value || type;

                                    return (
                                        <div key={type} style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            marginBottom: '2px',
                                            fontSize: '11px'
                                        }}>
                                            <span>{type}: {description}</span>
                                            <span style={{ fontWeight: '500' }}>{count}</span>
                                        </div>
                                    );
                                })}
                        </div>
                    )}
                </div>
            );
        }
        return null;
    };

    // Custom tooltip dla szczegółowego wykresu
    const DetailCustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload;
            const segmentType = hoveredSegment?.name === 'Rework' ? 'Rework' : 'NOK';
            const description = detailsDesc?.find(el => el.label === (segmentType === "NOK" ? data.name.toUpperCase() : data.name))?.value || data.name;

            return (
                <div style={{
                    backgroundColor: 'rgba(0, 0, 0, 0.8)',
                    color: 'white',
                    padding: '8px 12px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: '500',
                    zIndex: 1002,
                    pointerEvents: 'none',
                    whiteSpace: 'nowrap'
                }}>
                    {data.name}: {description} - {data.value} parts ({data.percentage}%)
                </div>
            );
        }
        return null;
    };

    // ===== DYNAMICZNA LEGENDA =====
    const CustomLegend = ({ payload }) => {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '24px',
                marginTop: '20px',
                flexWrap: 'wrap',
                fontSize: '13px',
                fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
            }}>
                {/* OK bef. rework */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                }}>
                    <div style={{
                        width: '12px',
                        height: '12px',
                        backgroundColor: '#4CAF50',
                        marginRight: '6px',
                        borderRadius: '2px'
                    }}></div>
                    <span style={{ color: 'rgb(52, 71, 103)', fontWeight: '500' }}>
                        OK bef. rework: {totalOK} ({okPercentage}%)
                    </span>
                </div>

                {/* Rework - tylko jeśli wartość > 0 */}
                {Rework > 0 && (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}>
                        <div style={{
                            width: '12px',
                            height: '12px',
                            backgroundColor: '#E07A00',
                            marginRight: '6px',
                            borderRadius: '2px'
                        }}></div>
                        <span style={{ color: 'rgb(52, 71, 103)', fontWeight: '500' }}>
                            Rework: {Rework} ({reworkPercentage}%)
                        </span>
                    </div>
                )}

                {/* NOK - tylko jeśli wartość > 0 */}
                {NOK > 0 && (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}>
                        <div style={{
                            width: '12px',
                            height: '12px',
                            backgroundColor: '#DC143C',
                            marginRight: '6px',
                            borderRadius: '2px'
                        }}></div>
                        <span style={{ color: 'rgb(52, 71, 103)', fontWeight: '500' }}>
                            NOK: {NOK} ({nokPercentage}%)
                        </span>
                    </div>
                )}
            </div>
        );
    };

    // Handle mouse events dla głównego wykresu
    const handleMainMouseEnter = (data) => {
        setHoveredSegment(data);
        if ((data.name === 'Rework' || data.name === 'NOK') && isVerySmallSegment(data.percentage)) {
            setShowDetailChart(true);
        }
    };

    const handleMainMouseLeave = () => {
        if (!shouldShowDetailChart) {
            setHoveredSegment(null);
            setShowDetailChart(false);
        }
    };

    // Handle mouse events dla szczegółowego wykresu
    const handleDetailMouseEnter = (data) => {
        setDetailHoveredSegment(data);
    };

    const handleDetailMouseLeave = () => {
        setDetailHoveredSegment(null);
        setShowDetailChart(false);
        setHoveredSegment(null);
    };

    // Styles
    const styles = {
        container: {
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            padding: '24px',
            margin: '16px',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            border: '1px solid rgba(0, 0, 0, 0.06)'
        },
        header: {
            marginBottom: '24px'
        },
        chartContainer: {
            backgroundColor: 'rgb(248, 249, 250)',
            borderRadius: '6px',
            padding: '24px',
            marginBottom: '28px',
            minHeight: '412px',
            display: 'flex',
            flexDirection: 'column'
        },
        statsContainer: {
            display: 'flex',
            justifyContent: 'space-around',
            alignItems: 'center',
            marginBottom: '24px',
            padding: '0 20px',
            flexWrap: 'wrap',
            gap: '16px'
        },
        statItem: {
            textAlign: 'center'
        },
        statNumber: {
            fontSize: '20px',
            fontWeight: '600',
            color: 'rgb(52, 71, 103)',
            margin: '0',
            fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
        },
        statLabel: {
            fontSize: '13px',
            color: 'rgb(123, 128, 154)',
            margin: '0',
            fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
        },
        detailChart: {
            position: 'absolute',
            right: '20px',
            top: '20px',
            width: '300px',
            height: '300px',
            backgroundColor: 'white',
            border: '2px solid #ddd',
            borderRadius: '12px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
            zIndex: 1001,
            padding: '16px',
            display: 'flex',
            flexDirection: 'column'
        },
        detailHeader: {
            fontSize: '14px',
            fontWeight: '600',
            color: 'rgb(52, 71, 103)',
            marginBottom: '8px',
            textAlign: 'center',
            borderBottom: '1px solid #eee',
            paddingBottom: '8px'
        },
        detailSubtitle: {
            fontSize: '12px',
            color: 'rgb(52, 71, 103)',
            marginBottom: '16px',
            textAlign: 'center',
            fontWeight: '600'
        }
    };

    const segmentType = hoveredSegment?.name === 'Rework' ? 'Rework' : 'NOK';
    const detailTotal = hoveredSegment?.name === 'Rework' ? Rework : NOK;

    return (
        <div style={styles.container}>
            <ChartHeader
                title={title}
                subtitle={subtitle}
                orderName={orderNumber}
                dateRange={dateRange}
            />

            <div style={styles.chartContainer}>
                <div style={styles.statsContainer}>
                    <div style={styles.statItem}>
                        <p style={styles.statNumber}>{totalOK}</p>
                        <p style={styles.statLabel}>OK bef. rework</p>
                    </div>
                    {Rework > 0 && (
                        <div style={styles.statItem}>
                            <p style={styles.statNumber}>{Rework}</p>
                            <p style={styles.statLabel}>Rework</p>
                        </div>
                    )}
                    {NOK > 0 && (
                        <div style={styles.statItem}>
                            <p style={styles.statNumber}>{NOK}</p>
                            <p style={styles.statLabel}>NOK</p>
                        </div>
                    )}
                    <div style={styles.statItem}>
                        <p style={styles.statNumber}>{grandTotal > 0 ? ((totalOK / grandTotal) * 100).toFixed(1) : 0}%</p>
                        <p style={styles.statLabel}>Int. Pass Rate</p>
                    </div>
                </div>

                <div style={{ position: 'relative', height: 320 }}>
                    {/* Główny wykres */}
                    <ResponsiveContainer width="100%" height={250}>
                        <PieChart>
                            <Pie
                                data={mainChartData}
                                cx="50%"
                                cy="50%"
                                innerRadius={40}
                                outerRadius={80}
                                stroke="none"
                                dataKey="value"
                                onMouseEnter={handleMainMouseEnter}
                                onMouseLeave={handleMainMouseLeave}
                            >
                                {mainChartData.map((entry, index) => (
                                    <Cell
                                        key={`cell-${index}`}
                                        fill={colors[entry.name]}
                                        stroke={hoveredSegment?.name === entry.name ? '#333' : 'none'}
                                        strokeWidth={hoveredSegment?.name === entry.name ? 2 : 0}
                                    />
                                ))}
                            </Pie>
                            <Tooltip content={<MainCustomTooltip />} />
                            <Legend content={<CustomLegend />} />
                        </PieChart>
                    </ResponsiveContainer>

                    {/* Szczegółowy wykres Rework/NOK */}
                    {shouldShowDetailChart && detailChartData.length > 0 && (
                        <div
                            style={styles.detailChart}
                            onMouseLeave={handleDetailMouseLeave}
                        >
                            <div style={styles.detailHeader}>
                                🔍 {segmentType} Details
                            </div>
                            <div style={styles.detailSubtitle}>
                                Total {segmentType}: {detailTotal} parts ({hoveredSegment.percentage}%)
                            </div>

                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={detailChartData}
                                        cx="50%"
                                        cy="50%"
                                        outerRadius={80}
                                        paddingAngle={2}
                                        dataKey="value"
                                        onMouseEnter={handleDetailMouseEnter}
                                    >
                                        {detailChartData.map((entry, index) => (
                                            <Cell
                                                key={`detail-cell-${index}`}
                                                fill={detailColors[entry.name] || '#999'}
                                                stroke={detailHoveredSegment?.name === entry.name ? '#333' : 'white'}
                                                strokeWidth={detailHoveredSegment?.name === entry.name ? 2 : 1}
                                                style={{ cursor: 'pointer' }}
                                            />
                                        ))}
                                    </Pie>
                                    <Tooltip content={<DetailCustomTooltip />} />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    )}

                    {/* Wskazówka dla małych segmentów */}
                    {(hoveredSegment?.name === 'Rework' || hoveredSegment?.name === 'NOK') &&
                        isVerySmallSegment(hoveredSegment.percentage) &&
                        !shouldShowDetailChart && (
                            <div style={{
                                fontSize: '11px',
                                color: '#999',
                                textAlign: 'center',
                                marginTop: '8px',
                                fontStyle: 'italic'
                            }}>
                                💡 Hover over {hoveredSegment?.name} area for detailed breakdown
                            </div>
                        )}
                </div>
            </div>
        </div>
    );
}

export default ChartOKNOKRework;